import React, { Component } from 'react'
import styled from 'styled-components'
import { Element as ScrollTo } from 'react-scroll'
import { math } from 'polished'
import { Box, Flex, Grid } from 'components/atoms/Layout'
import { Text, H3 } from 'components/atoms/Typography'
import { theme, fontWeights, radius, space } from 'config/theme'
import { COLORS, REGEX } from 'config/constants'
import { pxToRem } from 'utils/helpers'
import { db } from 'firebase-config'
import Input from 'components/atoms/Input'
import Button from 'components/atoms/Button'
import Popup from 'components/organisms/Popup'
import Container from 'components/atoms/Container'
import info from 'assets/images/info.svg'
import Tooltip from 'components/molecules/Tooltip'
import ModalContact from './ModalContact'
import DecorativeOr from './DecorativeOr'

const WARNING_TEXT = 'You have to enter either a phone number, or an email.'
const SERVER_ERROR_TEXT = 'An unknown network error has occurred'

const InfoImg = styled.img`
  display: block;
  width: ${pxToRem(13)};
  height: auto;
  cursor: pointer;
`

const Label = styled.label`
  font-size: ${theme.fontSizes.s};
  font-weight: ${fontWeights.semi_bold};
  color: ${COLORS.DARK_SAPHIRE};
`

const Form = styled.form`
  box-shadow: 7px 3px 35px -20px rgba(0, 0, 0, 0.75);
  border-radius: ${radius.m};
  background-color: ${COLORS.WHITE};
  max-width: ${pxToRem(500)};
  margin-right: auto;
  margin-left: auto;
`

const { FULL_NAME, EMAIL, TEL } = {
  FULL_NAME: 'fullName',
  EMAIL: 'email',
  TEL: 'tel',
}

const validateInput = (inputValue, isModalOpen) => {
  if (!inputValue && isModalOpen) return true
  if (inputValue) return true
  return false
}

const splitName = (name) => {
  const splitName = name.split(' ')
  return {
    firstName: splitName[0],
    lastName: splitName.length > 1 ? splitName[1] : '',
  }
}

const initialState = {
  [FULL_NAME]: '',
  [EMAIL]: '',
  [TEL]: '',
  isFullNameValid: true,
  isEmailValid: true,
  isTelValid: true,
  isWarningVisible: false,
  isModalOpen: false,
  isError: false,
  isTooltipOpen: false,
}

class ContactForm extends Component {
  state = initialState
  handleWarning = () => {
    this.setState({ isWarningVisible: true })
    setTimeout(() => this.setState({ isWarningVisible: false }), 4000)
  }
  handleToggleModal = () => {
    this.setState(({ isModalOpen }) => ({
      isModalOpen: !isModalOpen,
    }))
  }
  handleSubmitForm = (e) => {
    e.preventDefault()
    const {
      isEmailValid,
      isTelValid,
      isWarningVisible,
      fullName,
      email,
      tel,
    } = this.state
    if (isWarningVisible) return
    if ((isEmailValid && email !== '') || (isTelValid && tel !== '')) {
      const { firstName, lastName } = splitName(fullName)
      const contactsRef = db.ref('/contacts')
      const newContactRef = contactsRef.push()
      newContactRef
        .set({
          firstName,
          lastName,
          email,
          tel,
        })
        .then(() => this.handleToggleModal())
        .catch(() => {
          this.setState({ isError: true })
          this.handleWarning()
        })
    } else this.handleWarning()
  }
  handleBlur = (value, name) => {
    const { isModalOpen } = this.state
    if (name === EMAIL)
      this.setState({
        isEmailValid: validateInput(REGEX.EMAIL_VAL.test(value), isModalOpen),
      })
    if (name === TEL)
      this.setState({
        isTelValid: validateInput(
          REGEX.TEL_VAL.test(value) && value.length >= 9 && value.length <= 15,
          isModalOpen
        ),
      })
    if (name === FULL_NAME)
      this.setState({
        isFullNameValid: validateInput(
          value.split(' ').length === 2 && value.split(' ')[1] !== '',
          isModalOpen
        ),
      })
  }
  setStateWithInputValueByName = (name, value) => {
    this.setState(
      {
        [name]: value,
      },
      () => this.handleBlur(value, name)
    )
  }
  handleInputChange = ({ target: { name, value } }) => {
    if (name === TEL) {
      if (value === '' || REGEX.NUM_INPUT_VAL.test(value))
        this.setStateWithInputValueByName(name, value)
    } else {
      this.setStateWithInputValueByName(name, value)
    }
  }
  handleClick = () => {
    this.setState(({ isTooltipOpen }) => ({
      isTooltipOpen: !isTooltipOpen,
    }))
  }
  render() {
    const {
      fullName,
      email,
      tel,
      isFullNameValid,
      isEmailValid,
      isTelValid,
      isWarningVisible,
      isModalOpen,
      isError,
    } = this.state
    return (
      <React.Fragment>
        <Box data-aos="fade-up" py={{ tablet: 'l' }}>
          <Box bg={COLORS.INCREMENTAL_BLUE}>
            <Container>
              <Grid
                gridGap={space.l}
                gridTemplateColumns={{ mobile: '1fr', tablet: '1fr 1fr' }}
                py={{ mobile: 'xl', tablet: 'm' }}
              >
                <Flex
                  alignItems="center"
                  textAlign={{
                    mobile: 'center',
                    tablet: 'left',
                  }}
                  maxWidth={pxToRem(600)}
                  m="0 auto"
                  px="l"
                >
                  <Box flex="1">
                    <H3 color={COLORS.WHITE}>
                      Be one step closer to owning your Dream Home.
                    </H3>
                    <Text as="div" color={COLORS.WHITE} fontSize="l" mt="m">
                      100% LTV
                      <Box as="sup" display="inline-block" mx="xs">
                        <Tooltip
                          contentComp={
                            <Text
                              textAlign="center"
                              fontSize="xs"
                              color={COLORS.INCREMENTAL_BLUE}
                            >
                              The loan-to-value (LTV) ratio is a financial term
                              used by lenders to express the ratio of a loan to
                              the value of an asset purchased.
                            </Text>
                          }
                        >
                          <InfoImg src={info} alt="info-icon" />
                        </Tooltip>
                      </Box>
                    </Text>
                    <Text as="div" fontSize="l" color={COLORS.WHITE} mt="m">
                      100% financing
                      <Box as="sup" display="inline-block" mx="xs">
                        <Tooltip
                          contentComp={
                            <Text
                              textAlign="center"
                              fontSize="xs"
                              color={COLORS.INCREMENTAL_BLUE}
                            >
                              Financed by NuNu
                            </Text>
                          }
                        >
                          <InfoImg src={info} alt="info-icon" />
                        </Tooltip>
                      </Box>
                    </Text>
                    <Text color={COLORS.WHITE} fontSize="l" mt="m">
                      by NuNu
                    </Text>
                  </Box>
                </Flex>
                <Box my={{ tablet: `-${math(`${space.m} + ${space.l}`)}` }}>
                  <ScrollTo name="contact-form">
                    <Form onSubmit={this.handleSubmitForm}>
                      <Box p="l">
                        <Label>
                          <Flex justifyContent="space-between">
                            FULL NAME
                            {!isFullNameValid && (
                              <Text
                                fontSize="xs"
                                color={COLORS.RED_ORANGE_JUICE}
                              >
                                invalid name
                              </Text>
                            )}
                          </Flex>
                          <Input
                            onChange={this.handleInputChange}
                            onBlur={() => this.handleBlur(fullName, FULL_NAME)}
                            isValid={isFullNameValid}
                            placeholder="Full Name"
                            value={this.state[FULL_NAME]}
                            name={FULL_NAME}
                            width="100%"
                            p="m"
                          />
                        </Label>
                        <Box mt="l" mb="m">
                          <Label>
                            <Flex justifyContent="space-between">
                              EMAIL ADDRESS
                              {!isEmailValid && (
                                <Text
                                  fontSize="xs"
                                  color={COLORS.RED_ORANGE_JUICE}
                                >
                                  invalid email
                                </Text>
                              )}
                            </Flex>
                            <Input
                              onChange={this.handleInputChange}
                              onBlur={() => this.handleBlur(email, EMAIL)}
                              isValid={isEmailValid}
                              placeholder="Email Address"
                              type="email"
                              value={this.state[EMAIL]}
                              name={EMAIL}
                              width="100%"
                              p="m"
                            />
                          </Label>
                        </Box>
                        <DecorativeOr />
                        <Box>
                          <Label>
                            <Flex justifyContent="space-between">
                              PHONE NUMBER
                              {!isTelValid && (
                                <Text
                                  fontSize="xs"
                                  color={COLORS.RED_ORANGE_JUICE}
                                >
                                  invalid phone number
                                </Text>
                              )}
                            </Flex>
                            <Input
                              onChange={this.handleInputChange}
                              onBlur={() => this.handleBlur(tel, TEL)}
                              isValid={isTelValid}
                              placeholder="Phone Number"
                              type="tel"
                              value={this.state[TEL]}
                              name={TEL}
                              width="100%"
                              p="m"
                            />
                          </Label>
                        </Box>
                        <Button.Filled type="submit" mt="l" width="100%">
                          CONTINUE
                        </Button.Filled>
                      </Box>
                    </Form>
                  </ScrollTo>
                </Box>
              </Grid>
            </Container>
          </Box>
        </Box>
        <Popup
          isVisible={isWarningVisible}
          message={isError ? SERVER_ERROR_TEXT : WARNING_TEXT}
        />
        <ModalContact
          onClose={this.handleToggleModal}
          isModalOpen={isModalOpen}
        />
      </React.Fragment>
    )
  }
}

export default ContactForm
